import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { GetRef, Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { OptionType } from "../../../interfaces/classifier";
import { useAppSelector } from "../../../../app";
import { EventTriggerType } from "../../../interfaces/categorizationEvents";
import { ICategorization } from "../../../../configMap/interfaces/category";
import useKeyEvent from "../../../../configMap/hooks/useKeyEvent";
import type { CallEvents } from "../../../hooks/useCategorizationEvents";
import { Keys } from "../../../../common/constants";
import Loader from "../../../../common/loader/Loader";
import useDelayedFlag from "../../../hooks/useDelayedFlag";
import { VALUE_LOADING_INDICATOR_DELAY_MS } from "../../../constants";

type SelectRef = GetRef<typeof Select>;

type Props = {
  categorization: ICategorization;
  value: string | undefined;
  visibleValue?: string;
  onFocus: () => void;
  onBlur: () => void;
  onSearch: (value: string) => void;
  filterOption: (input: string, option?: DefaultOptionType) => boolean;
  notFoundContent?: ReactNode;
  disabled?: boolean;
  className?: string;
  status?: "" | "warning" | "error" | undefined;
  loading?: boolean;
  isLoadingValue?: boolean;
  tabId: string;
  categorizationOptions: Array<OptionType>;
  handleChange: (key: string, callback: () => void) => Promise<void>;
  callEvents: CallEvents;
};

const { Option } = Select;

const mapping = { value: "key" };

const CategorizationSelect: FC<Props> = ({
  categorization,
  value,
  visibleValue,
  onFocus,
  onBlur,
  onSearch,
  filterOption,
  notFoundContent,
  disabled,
  className,
  status,
  loading,
  isLoadingValue = false,
  tabId,
  categorizationOptions,
  handleChange,
  callEvents,
}) => {
  const ref = useRef<SelectRef | null>(null);

  const { activeButton, isAnnotating } = useAppSelector(
    (state) => state.appReducer,
  );

  const showValueLoading = useDelayedFlag(
    isLoadingValue,
    VALUE_LOADING_INDICATOR_DELAY_MS,
  );

  const handleKeyEvent = useKeyEvent(Keys.ESCAPE, () => ref.current!.blur());

  useEffect(() => {
    if (!activeButton || !ref.current) {
      return;
    }

    if (activeButton.id !== tabId) {
      return;
    }

    ref.current!.focus();
  }, [activeButton, tabId]);

  useEffect(() => {
    if (isAnnotating) {
      ref.current!.blur();
    }
  }, [isAnnotating]);

  const selectClassName = useMemo(() => {
    const classes = [className];

    if (loading) {
      classes.push("loading-select-prominent");
    }

    if (disabled) {
      classes.push("classifier-item__select-disabled");
    }

    return classes.filter(Boolean).join(" ");
  }, [className, loading, disabled]);

  // The Select uses `fieldNames={{ value: "key" }}` so it matches options by
  // their `key` (UUID). Callers pass the annotation's value (e.g. "628"),
  // so translate it to the corresponding option key for the match to succeed
  // and the option's label to be displayed in the closed state.
  const selectValue = useMemo(() => {
    if (value == null) {
      return null;
    }
    const found = categorizationOptions.find((o) => o.value === value);
    return found?.key ?? value;
  }, [categorizationOptions, value]);

  const displayLabel = Boolean(categorization.search?.displayLabel);

  // By default the closed select shows the stored value (e.g. a customer
  // number), with visibleValue as a fallback. With `search.displayLabel` the
  // value is a bare record id (e.g. "14"), so prefer the matched option's
  // label; the fallbacks cover the window before the lazily-fetched options
  // arrive. `props.value` is last because `fieldNames` maps it to the option
  // key, a UUID.
  const renderLabel = useCallback(
    (props: { label?: ReactNode; value?: string | number }) => {
      if (displayLabel) {
        return props.label ?? visibleValue ?? value ?? props.value;
      }
      return value ?? visibleValue ?? props.value;
    },
    [displayLabel, value, visibleValue],
  );

  return (
    <Select
      ref={ref}
      allowClear
      value={selectValue}
      labelRender={renderLabel}
      classNames={{ popup: { root: "categorization-select-dropdown" } }}
      suffixIcon={showValueLoading ? <Loader size="small" /> : undefined}
      placeholder={showValueLoading ? "Loading..." : "Select a value"}
      optionFilterProp="children"
      onChange={(selectedKey) => {
        const found = categorizationOptions.find((o) => o.key === selectedKey);
        const callback = () => {
          if (found) {
            callEvents(
              EventTriggerType.ON_SELECT,
              categorization,
              found.value,
              found.visibleValue,
            );
            ref.current?.blur();
          }
        };
        handleChange(selectedKey, callback);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      showSearch={{
        onSearch,
        filterOption,
        optionFilterProp: "children",
      }}
      notFoundContent={notFoundContent}
      fieldNames={mapping}
      disabled={disabled}
      className={selectClassName}
      status={status}
      loading={loading}
      onKeyDown={handleKeyEvent}
    >
      {(categorizationOptions ?? []).map((o: OptionType) => {
        const { label, value, key } = o;
        const split = label.split(/\n/);

        if (split.length > 1) {
          return (
            <Option key={key} value={value} label={label}>
              <span className="categorization-option__label" title={label}>
                {split.map((s, i) => (
                  <React.Fragment key={s}>
                    {i > 0 && <br />}
                    {s}
                  </React.Fragment>
                ))}
              </span>
            </Option>
          );
        }

        return (
          <Option key={key} value={value} label={label}>
            <span className="categorization-option__label" title={label}>
              {label}
            </span>
          </Option>
        );
      })}
    </Select>
  );
};

export default CategorizationSelect;
